import React, { useContext } from 'react';
import styled from 'themed-components';
import { Table, TableBody, TableHead, TableCell, TableContainer, TableFooter, TablePagination, TableRow } from 'ui-library/table';
import { LockIcon, LockOpenIcon, LastPageIcon, KeyboardArrowRight, KeyboardArrowLeft, FirstPageIcon } from 'ui-library/icons';
import { IconButton, Button } from 'ui-library';
import { useHistory } from 'react-router-dom';
import Paper from '@material-ui/core/Paper';
import { map, size, slice } from 'lodash';
import { MetadataDto, useGetMail } from 'api';
import { Grid, Typography } from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import moment from 'moment-timezone';
import { Pagination } from '@material-ui/lab';
import { UserProfileContext } from 'context/UserProfileContext';

type OnMailClick = (entityId?: string) => void;

const StyledLockIcon = styled(LockIcon)`
	color: #ee5253;
`;

const StyledLockOpenIcon = styled(LockOpenIcon)`
	color: #1dd1a1;
`;

interface MailRowProps {
	id: string;
	filename: string;
	status: string;
}

interface TablePaginationActionsProps {
	count: number;
	page: number;
	rowsPerPage: number;
	onChangePage: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

const TablePaginationActions = (props: TablePaginationActionsProps) => {
	const { count, page, rowsPerPage, onChangePage } = props;

	const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		onChangePage(event, 0);
	};

	const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		onChangePage(event, page - 1);
	};

	const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		onChangePage(event, page + 1);
	};

	const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
	};

	return (
		<>
			<IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
				<FirstPageIcon />
			</IconButton>
			<IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
				<KeyboardArrowLeft />
			</IconButton>
			<IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
				<KeyboardArrowRight />
			</IconButton>
			<IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
				<LastPageIcon />
			</IconButton>
		</>
	);
};

interface MailroomTableRowProps {
	row: MetadataDto;
	onClick: OnMailClick;
	userId?: number;
}

const MailroomTableRow = ({ userId, row, onClick }: MailroomTableRowProps) => {
	const lockedByMe = !!userId && userId == row.lockedByUserId;
	const locked = !!row.lockedAt;
	return (
		<TableRow>
			<TableCell style={{ width: 200 }} component="th" scope="row">
				{moment(row.documentCreatedAt).format('DD/MM/yyyy hh:mm A')}
			</TableCell>
			<TableCell style={{ width: 320 }} component="th" scope="row">
				{row.entityId}
			</TableCell>

			<TableCell align="left">{row.artefactFilename}</TableCell>

			<TableCell style={{ width: 120 }} align="center">
				{locked ? <StyledLockIcon /> : <StyledLockOpenIcon />}
			</TableCell>

			<TableCell style={{ width: 120 }} align="center">
				<Button disabled={locked && !lockedByMe} variant="contained" color="primary" onClick={(_) => onClick(row.entityId)}>
					Open
				</Button>
			</TableCell>
		</TableRow>
	);
};

interface EmptyRowsProps {
	emptyRows: number;
}
const EmptyRows = ({ emptyRows }: EmptyRowsProps) => {
	return (
		<TableRow style={{ height: 53 * emptyRows }}>
			<TableCell></TableCell>
		</TableRow>
	);
};

interface NoDataProps {
	emptyRows: number;
	onRefreshClick: () => void;
}
const NoData = ({ emptyRows, onRefreshClick }: NoDataProps) => {
	return (
		<>
			<TableRow>
				<TableCell colSpan={4} align="left">
					<Typography variant="h4">{"You don't have mail.."}</Typography>
					<IconButton onClick={onRefreshClick}>
						<Refresh />
					</IconButton>
				</TableCell>
			</TableRow>
			<TableRow style={{ height: 53 * emptyRows - 1 }}></TableRow>
		</>
	);
};
interface TableRowsProps {
	rowsPerPage: number;
	page: number;
	onClick: OnMailClick;
	data: MetadataDto[];
	userId?: number;
}

const TableRows = ({ userId, rowsPerPage, page, onClick, data }: TableRowsProps) => {
	const emptyRows = rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);
	const sliceFrom = page * rowsPerPage;
	const sliceTo = page * rowsPerPage + rowsPerPage;

	return (
		<React.Fragment>
			{map(rowsPerPage > 0 ? slice(data, sliceFrom, sliceTo) : data, (row) => (
				<MailroomTableRow key={row.metadataId} userId={userId} row={row} onClick={onClick} />
			))}
			{emptyRows > 0 && <EmptyRows emptyRows={emptyRows} />}
		</React.Fragment>
	);
};

const StyledPaper = styled(Paper)`
	padding: 2rem;
`;

export const MailroomView = () => {
	const defaultRowsPerPage = 10;
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage);

	const history = useHistory<string>();

	const { data: pagedData, isLoading, isFetched, refetch } = useGetMail({ pageSize: rowsPerPage, currentPage: page });
	const { userId } = useContext(UserProfileContext);
	if (isLoading || !isFetched) return null;

	const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
		setPage(newPage);
	};

	const data = pagedData?.results;

	const handleOnClick = (id: string | undefined) => {
		history.push(`/mail/${id}`);
	};

	const columns = [
		<TableCell key="createdAt">Created At</TableCell>,
		<TableCell key="Id">Id</TableCell>,
		<TableCell key="filename">Filename</TableCell>,
		<TableCell key="status" align="center">
			Status
		</TableCell>,
		<TableCell key="cta" align="center">
			Open
		</TableCell>
	];

	const onRefreshClick = () => {
		refetch();
	};
	return (
		<StyledPaper>
			<TableContainer component={Paper}>
				<Table aria-label="custom pagination table">
					<TableHead>
						<TableRow>{columns}</TableRow>
					</TableHead>
					<TableBody>
						{size(data) === 0 && <NoData onRefreshClick={onRefreshClick} emptyRows={rowsPerPage} />}
						{/* eslint-disable-next-line max-len */}
						{size(data) > 0 && <TableRows userId={userId} rowsPerPage={rowsPerPage} page={0} onClick={handleOnClick} data={data || []} />}
					</TableBody>
					<TableFooter>
						<TableRow>
							<TableCell align="right" colSpan={size(columns)}>
								<Grid xs={12} justify="flex-end" alignContent="flex-end" direction="row" container>
									<Pagination page={page} count={pagedData?.pageCount} color="primary" onChange={handleChangePage} />
								</Grid>
							</TableCell>
						</TableRow>
					</TableFooter>
				</Table>
			</TableContainer>
		</StyledPaper>
	);
};
